export type ClientActionStatus = 'overdue' | 'soon' | 'upcoming' | 'recurring';

export interface ClientFollowUpAction {
  source: string;
  type: string;
  eventName: string | null;
  title: string;
  /** YYYY-MM-DD, in Paris time. */
  date: string;
  status: ClientActionStatus;
  /** `name` is null when the user no longer exists. */
  assignedUser: { id: number; name: string | null } | null;
}

export interface ClientNextAction {
  companyId: number;
  hasPlanning: boolean;
  overdueCount: number;
  /** The oldest overdue action, otherwise the soonest one. */
  nextAction: ClientFollowUpAction | null;
  /** The soonest action that is not overdue: what comes next when `nextAction` is overdue. */
  upcomingAction: ClientFollowUpAction | null;
}

export interface CalendarAction extends ClientFollowUpAction {
  companyId: number;
  companyName: string;
}

export const CLIENT_ACTION_STATUS_LABELS: Record<ClientActionStatus, string> = {
  overdue: 'En retard',
  soon: 'Bientôt',
  upcoming: 'À venir',
  recurring: 'Récurrent',
};

export const CLIENT_ACTION_STATUS_CLASSES: Record<ClientActionStatus, string> = {
  overdue: 'bg-red-100 text-red-800',
  soon: 'bg-orange-100 text-orange-800',
  upcoming: 'bg-blue-100 text-blue-800',
  recurring: 'bg-gray-100 text-gray-700',
};

/** The types the sources return. A new source adds its types here to make them filterable. */
export const CLIENT_ACTION_TYPES = [
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'referent-mails', label: 'Campagne référent' },
  { value: 'sci-mails', label: 'Campagne SCI' },
  { value: 'boitage', label: 'Boîtage' },
  { value: 'dpe', label: 'DPE (chaque mercredi)' },
] as const;

/** Every client has it every week: shown only when asked for, it would bury the rest. */
export const RECURRING_ACTION_TYPE = 'dpe';

/** "2026-11-05" → "05/11/2026", without a Date: a calendar day has no time zone. */
export const formatActionDay = (day: string): string => day.split('-').reverse().join('/');

export const NO_ACTION_FILTER = 'none';
export const UNASSIGNED_FILTER = 'unassigned';

export const assignedUserLabel = (user: ClientFollowUpAction['assignedUser']): string => {
  if (!user) {return 'Non assigné';}
  return user.name ?? `Utilisateur n°${user.id}`;
};

export const emptyActionLabel = (followUp: ClientNextAction): string => (
  followUp.hasPlanning ? 'Aucune échéance ouverte' : 'Aucun planning'
);

/** `status`: a status, NO_ACTION_FILTER or ''. `assignee`: a user id, UNASSIGNED_FILTER or ''. */
export const matchesFollowUpFilters = (
  followUp: ClientNextAction | undefined,
  status: string,
  assignee: string,
): boolean => {
  const action = followUp?.nextAction ?? null;
  if (status && (action?.status ?? NO_ACTION_FILTER) !== status) {return false;}
  if (!assignee) {return true;}
  if (!action) {return false;}
  return assignee === UNASSIGNED_FILTER
    ? action.assignedUser === null
    : String(action.assignedUser?.id) === assignee;
};

export interface AssigneeOption {
  value: string;
  label: string;
}

/** Everyone who appears in these actions, by name, "Non assigné" first when it applies. */
export const assigneeOptions = (actions: ClientFollowUpAction[]): AssigneeOption[] => {
  const users = new Map<number, string>();
  let hasUnassigned = false;
  for (const action of actions) {
    if (action.assignedUser) {
      users.set(action.assignedUser.id, assignedUserLabel(action.assignedUser));
    } else {
      hasUnassigned = true;
    }
  }
  const options = [...users.entries()]
    .map(([id, label]) => ({ value: String(id), label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  return hasUnassigned ? [{ value: UNASSIGNED_FILTER, label: 'Non assigné' }, ...options] : options;
};

export interface CalendarFilters {
  companyId: number | null;
  type: string;
  assignee: string;
}

export const filterCalendarActions = (actions: CalendarAction[], filters: CalendarFilters): CalendarAction[] => actions.filter((action) => (
  (filters.companyId === null || action.companyId === filters.companyId)
  && (!filters.type || action.type === filters.type)
  && matchesFollowUpFilters({ companyId: action.companyId, hasPlanning: true, overdueCount: 0, nextAction: action, upcomingAction: null }, '', filters.assignee)
));

export const groupActionsByDay = <T extends ClientFollowUpAction>(actions: T[]): Map<string, T[]> => {
  const byDay = new Map<string, T[]>();
  for (const action of actions) {
    byDay.set(action.date, [...(byDay.get(action.date) ?? []), action]);
  }
  return byDay;
};

export interface CalendarDay {
  day: string;
  dayOfMonth: number;
  inMonth: boolean;
}

const toDay = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const todayKey = (now: Date = new Date()): string => toDay(now);

/** The weeks of a month, Monday first, with the days of the months around to fill them. */
export const buildMonthGrid = (year: number, month: number): CalendarDay[][] => {
  const first = new Date(year, month, 1);
  const daysBeforeFirst = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weekCount = Math.ceil((daysBeforeFirst + daysInMonth) / 7);

  return Array.from({ length: weekCount }, (_, week) => Array.from({ length: 7 }, (__, weekDay) => {
    const date = new Date(year, month, 1 - daysBeforeFirst + (week * 7) + weekDay);
    return { day: toDay(date), dayOfMonth: date.getDate(), inMonth: date.getMonth() === month };
  }));
};
